import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Text } from '@mantine/core';
import { ModalFooter } from '@/components/ModalFooter/ModalFooter';
import { useScreenSize } from '@/hooks';
import { UNSAVED_CHANGES_MODAL_VARIANT } from '@/types';
import styles from './UnsavedChangesModal.module.sass';

type UnsavedChangesModalProps = {
  opened: boolean;
  onClose: () => void;
  onLeave: () => void;
  variant?: UNSAVED_CHANGES_MODAL_VARIANT;
  onSave?: () => void;
  isSaveLoading?: boolean;
  saveChangesLabel?: string;
  secondaryActions?: ReactNode[];
};

export const UnsavedChangesModal = ({
  opened,
  onClose,
  onLeave,
  variant = UNSAVED_CHANGES_MODAL_VARIANT.OVERLAY_GUARD,
  onSave,
  isSaveLoading = false,
  saveChangesLabel,
  secondaryActions,
}: UnsavedChangesModalProps) => {
  const { t } = useTranslation('global');
  const { isDesktop } = useScreenSize();
  const isSaveChangesVariant = variant === UNSAVED_CHANGES_MODAL_VARIANT.SAVE_CHANGES;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('unsavedChangesModal.title')}
      size="md"
      fullScreen={!isDesktop}
      centered
      closeOnClickOutside={false}
      zIndex={400}
    >
      <Text className={styles.message}>{t('unsavedChangesModal.message')}</Text>

      <ModalFooter
        secondaryActions={[
          ...(secondaryActions ?? []),
          <Button key="leave" variant="outline" color="fail" size="sm" onClick={onLeave}>
            {t('unsavedChangesModal.leaveWithoutSaving')}
          </Button>,
        ]}
        mainAction={
          isSaveChangesVariant ? (
            <Button variant="filled" color="brand" size="sm" onClick={onSave} loading={isSaveLoading}>
              {saveChangesLabel ?? t('unsavedChangesModal.saveChanges')}
            </Button>
          ) : (
            <Button variant="filled" color="brand" size="sm" onClick={onClose}>
              {t('unsavedChangesModal.goBack')}
            </Button>
          )
        }
      />
    </Modal>
  );
};
